import React, { useState } from "react";
import { X } from "lucide-react";
import axios from "../../routers/axios";
import { Editor } from "@tinymce/tinymce-react";

const CreateScenarioModal = ({ test, isOpen, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    scenario_name: "",
    context: "",
    duration_minutes: "",
    level: "B2", // mặc định
    topic: "",
    instructor_name: "",
    scenario_order: "",
    instruction_introduction: "",
    instruction_task_sequence: "",
    instruction_overview: "",
    instruction_key_instructions: "",
    total_time: "",
    reading_time: "",
    writing_time: "",
    ai_instructor: "",
    video_instructions: "",
    interactive_elements: "",
  });

  const handleEditorChange = (field) => (content) => {
    setFormData((prev) => ({ ...prev, [field]: content }));
  };

  const handleNumberChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value ? Number(value) : "",
    }));
  };

  const handleLevelChange = (e) => {
    setFormData((prev) => ({ ...prev, level: e.target.value }));
  };

  const handleSubmit = async () => {
    try {
      const cleanPayload = Object.fromEntries(
        Object.entries({ ...formData, test_id: test.id }).map(([k, v]) => {
          if (v === "" || v === null || v === undefined) return [k, null];
          return [k, v];
        })
      );

      console.log("[CreateScenarioModal] Payload gửi đi:", cleanPayload);

      const res = await axios.post("/scenarios/create", cleanPayload);
      console.log("✅ [CreateScenarioModal] Phản hồi API:", res.data);
      alert("Tạo scenario thành công!");
      onCreated && onCreated();
      onClose();
    } catch (err) {
      console.error("❌ [CreateScenarioModal] Lỗi tạo scenario:", err);
      if (err.response) console.error("📩 Response data:", err.response.data);
      alert("Lỗi khi tạo scenario! Xem console để biết thêm chi tiết.");
    }
  };

  if (!isOpen) return null;

  const baseEditorConfig = {
    height: 300,
    menubar: true,
    plugins: [
      "advlist",
      "autolink",
      "lists",
      "link",
      "image",
      "charmap",
      "preview",
      "anchor",
      "searchreplace",
      "visualblocks",
      "code",
      "fullscreen",
      "insertdatetime",
      "media",
      "table",
      "help",
      "wordcount",
    ],
    toolbar:
      "undo redo | blocks | " +
      "bold italic underline forecolor | alignleft aligncenter alignright alignjustify | " +
      "bullist numlist outdent indent | link image media table | code fullscreen | help",
    content_style:
      "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
    paste_data_images: true,
  };

  const renderEditor = (label, field, placeholder) => (
    <div className="mb-5">
      <label className="block text-sm font-semibold mb-2">{label}</label>
      <Editor
        apiKey="a7bzgpdrc4fmam222696wcyaaybnyn3vmqk9wk86wamc80as"
        init={{
          ...baseEditorConfig,
          placeholder,
        }}
        value={formData[field]}
        onEditorChange={handleEditorChange(field)}
      />
    </div>
  );

  const renderNumberInput = (label, field, placeholder) => (
    <div className="mb-5">
      <label className="block text-sm font-semibold mb-2">{label}</label>
      <input
        type="number"
        className="w-full border rounded-lg p-2"
        placeholder={placeholder}
        value={formData[field]}
        onChange={handleNumberChange(field)}
      />
    </div>
  );

  const renderLevelSelector = () => {
    const levels = ["A1", "A2", "B1", "B2", "C1", "C2"];
    return (
      <div className="mb-5">
        <label className="block text-sm font-semibold mb-2">Trình độ (Level)</label>
        <div className="flex flex-wrap gap-4">
          {levels.map((lvl) => (
            <label
              key={lvl}
              className={`flex items-center gap-2 border px-3 py-2 rounded-lg cursor-pointer ${
                formData.level === lvl ? "bg-blue-600 text-white" : "hover:bg-gray-100"
              }`}
            >
              <input
                type="radio"
                name="level"
                value={lvl}
                checked={formData.level === lvl}
                onChange={handleLevelChange}
                className="hidden"
              />
              {lvl}
            </label>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-6xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-bold">
            Tạo Scenario mới cho Test:{" "}
            <span className="text-blue-600">{test?.test_name}</span>
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          {renderEditor("Tên Scenario", "scenario_name", "Ví dụ: Scenario 1 - Climate Change Discussion")}
          {renderEditor("Ngữ cảnh (Context)", "context", "Mô tả tình huống hoặc bối cảnh tổng quan.")}
          {renderNumberInput("Thời lượng (Duration Minutes)", "duration_minutes", "Nhập tổng thời gian (ví dụ: 70)")}
          {renderLevelSelector()}
          {renderEditor("Chủ đề (Topic)", "topic", "Ví dụ: Environmental Issues, Education Technology...")}
          {renderEditor("Người hướng dẫn (Instructor Name)", "instructor_name", "Tên giảng viên hoặc trợ giảng AI.")}
          {renderNumberInput("Thứ tự Scenario (Order)", "scenario_order", "Nhập số thứ tự (1, 2, 3, ...)")}
          {renderEditor("Giới thiệu (Instruction Introduction)", "instruction_introduction", "Giới thiệu tổng quan.")}
          {renderEditor("Chuỗi nhiệm vụ (Task Sequence)", "instruction_task_sequence", "Liệt kê các nhiệm vụ.")}
          {renderEditor("Tổng quan nhiệm vụ (Overview)", "instruction_overview", "Tóm tắt nội dung chính.")}
          {renderEditor("Hướng dẫn chính (Key Instructions)", "instruction_key_instructions", "Các hướng dẫn quan trọng.")}
          {renderEditor("Tổng thời gian (Total Time)", "total_time", "Ví dụ: 70 phút tổng cộng.")}
          {renderEditor("Thời gian Reading", "reading_time", "Ví dụ: 30 phút.")}
          {renderEditor("Thời gian Writing", "writing_time", "Ví dụ: 40 phút.")}
          {renderEditor("AI Instructor", "ai_instructor", "Tên hoặc mô tả về AI Instructor.")}
          {renderEditor("Video Instructions", "video_instructions", "Link video hoặc mô tả video.")}
          {renderEditor("Interactive Elements", "interactive_elements", "Các hoạt động tương tác đặc biệt.")}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border hover:bg-gray-50">
            Hủy
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            Lưu
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateScenarioModal;
